import { Action, Reducer, ActionCreator } from 'redux';
import { AppThunkAction } from './';
import * as Sentry from '@sentry/browser';

export const AUTH_STORAGE_KEY = 'ACCESS_TOKEN';

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface AuthenticationState {
    token: string;
    subdomain: string;
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.

interface GetUserTokenAction {
    type: 'GET_USER_TOKEN';
}

interface SaveUserTokenAction {
    type: 'SAVE_USER_TOKEN';
    token: string;
    subdomain: string;
}

interface ClearUserTokenAction {
    type: 'CLEAR_USER_TOKEN';
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction = GetUserTokenAction | SaveUserTokenAction | ClearUserTokenAction;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

export const actionCreators = {
    getUserToken: (tenant: string, username: string, password: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        let fetchTask = fetch(`api/User/Login`, {
            body: JSON.stringify({ tenant, username, password }),
            headers: {
                'content-type': 'application/json'
            },
            method: 'POST',
            redirect: 'error'
        })
            .then(response => response.json() as Promise<{ token: string}>)
            .then((data) => {
                Sentry.configureScope((scope) => {
                  scope.setUser({"username": username});
                });
                dispatch({ type: 'SAVE_USER_TOKEN', token: data.token, subdomain: tenant });
            })
            .catch((error) => {
              alert('Invalid Username/Password!');
            });

        dispatch({ type: 'GET_USER_TOKEN' });
    },

    clearUserToken: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        dispatch({ type: 'CLEAR_USER_TOKEN' });
    }
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.

const unloadedState: AuthenticationState = {
  token: (typeof localStorage !== 'undefined') ? localStorage.getItem(AUTH_STORAGE_KEY) || '' : '',
  subdomain: ''
};

export const reducer: Reducer<AuthenticationState> = (state: AuthenticationState, incomingAction: Action) => {
    const action = incomingAction as KnownAction;
    switch (action.type) {
        case 'GET_USER_TOKEN':
            return {
                token: state.token,
                subdomain: state.subdomain,
            };
        case 'SAVE_USER_TOKEN':
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem(AUTH_STORAGE_KEY, action.token);
            }
            return {
                token: action.token,
                subdomain: action.subdomain,
            };
        case 'CLEAR_USER_TOKEN':
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem(AUTH_STORAGE_KEY, '');
            }
            return {
                token: '',
                subdomain: state.subdomain,
            };
        default:
            // The following line guarantees that every action in the KnownAction union has been covered by a case above
            const exhaustiveCheck: never = action;
    }

    return state || unloadedState;
};
